import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderOpenIcon,
  BellIcon,
  PlusCircleIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  ChartBarSquareIcon,
  ComputerDesktopIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { fetchCases, fetchAlerts } from '../api/client';
import Badge from './ui/Badge';

function StatBox({ value, label, color, loading }) {
  if (loading) {
    return (
      <div className="bg-gray-800/60 rounded-lg p-3 text-center">
        <div className="h-7 w-10 bg-gray-700 rounded animate-pulse mx-auto mb-1" />
        <div className="h-3 w-12 bg-gray-700 rounded animate-pulse mx-auto" />
      </div>
    );
  }
  return (
    <div className="bg-gray-800/60 rounded-lg p-3 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const [cases, setCases] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [casesResult, alertsResult] = await Promise.allSettled([
        fetchCases(),
        fetchAlerts(null, { hours: 72 }),
      ]);
      if (cancelled) return;
      if (casesResult.status === 'fulfilled') setCases(casesResult.value);
      if (alertsResult.status === 'fulfilled') setAlerts(alertsResult.value);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Case counts
  const caseOpen = cases?.filter(c => c.status === 'Open').length ?? 0;
  const caseInProgress = cases?.filter(c => c.status === 'InProgress').length ?? 0;
  const caseClosed = cases?.filter(c => c.status === 'Closed').length ?? 0;

  // Alert counts (72h, all tenants — excludes test alerts)
  const liveAlerts = alerts?.filter(a => a.status !== 'test') ?? [];
  const alertCritical = liveAlerts.filter(a => a.severity === 'critical').length;
  const alertHigh = liveAlerts.filter(a => a.severity === 'high').length;
  const alertMedium = liveAlerts.filter(a => a.severity === 'medium').length;
  const alertLow = liveAlerts.filter(a => a.severity === 'low').length;
  const alertNew = liveAlerts.filter(a => a.status === 'new').length;
  const alertUrgent = alertCritical + alertHigh;

  return (
    <div className="p-8 max-w-6xl mx-auto">

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-xl font-bold text-gray-100">Security Operations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Investigate compromises, review detection alerts, or analyze device coverage
        </p>
      </div>

      {/* Workspace cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">

        {/* Alerts card */}
        <div className="bg-gray-900 border border-gray-800 hover:border-red-500/40 rounded-xl p-6 flex flex-col transition-colors">
          <div className="flex items-start gap-3 mb-5">
            <div className="p-2.5 bg-red-600/15 rounded-lg flex-shrink-0">
              <BellIcon className="h-6 w-6 text-red-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-100">Alerts</h2>
                {!loading && alertNew > 0 && (
                  <Badge tone="critical">{alertNew} new</Badge>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Detection alerts — last 72 hours</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 mb-5">
            <StatBox value={alertCritical} label="Critical" color="text-red-400"    loading={loading && alerts === null} />
            <StatBox value={alertHigh}     label="High"     color="text-orange-400" loading={loading && alerts === null} />
            <StatBox value={alertMedium}   label="Medium"   color="text-amber-400"  loading={loading && alerts === null} />
            <StatBox value={alertLow}      label="Low"      color="text-yellow-400" loading={loading && alerts === null} />
          </div>

          {!loading && alertUrgent > 0 && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-red-950/40 border border-red-800/40 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
              <p className="text-xs text-red-300">
                {alertUrgent} critical or high severity alert{alertUrgent !== 1 ? 's' : ''} require{alertUrgent === 1 ? 's' : ''} attention
              </p>
            </div>
          )}

          <div className="mt-auto">
            <Link
              to="/alerts"
              className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2 px-4 rounded-lg bg-red-600/15 hover:bg-red-600/25 text-red-400 border border-red-500/30 transition-colors"
            >
              View Alerts
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Investigations card */}
        <div className="bg-gray-900 border border-gray-800 hover:border-blue-500/40 rounded-xl p-6 flex flex-col transition-colors">
          <div className="flex items-start gap-3 mb-5">
            <div className="p-2.5 bg-blue-600/15 rounded-lg flex-shrink-0">
              <FolderOpenIcon className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-100">Investigations</h2>
              <p className="text-xs text-gray-500 mt-0.5">Case management &amp; log analysis</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-5">
            <StatBox value={caseOpen}       label="Open"        color="text-amber-400" loading={loading && cases === null} />
            <StatBox value={caseInProgress} label="In Progress" color="text-blue-400"  loading={loading && cases === null} />
            <StatBox value={caseClosed}     label="Closed"      color="text-gray-400"  loading={loading && cases === null} />
          </div>

          <div className="mt-auto flex gap-2">
            <Link
              to="/cases"
              className="flex-1 btn-primary flex items-center justify-center gap-2 text-sm"
            >
              View Cases
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link
              to="/cases/new"
              className="btn-secondary flex items-center gap-2 px-3 text-sm"
              title="New Case"
            >
              <PlusCircleIcon className="h-5 w-5" />
            </Link>
          </div>
        </div>

        {/* Coverage Analysis card */}
        <div className="bg-gray-900 border border-gray-800 hover:border-purple-500/40 rounded-xl p-6 flex flex-col transition-colors">
          <div className="flex items-start gap-3 mb-5">
            <div className="p-2.5 bg-purple-600/15 rounded-lg flex-shrink-0">
              <ChartBarSquareIcon className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-100">Coverage Analysis</h2>
              <p className="text-xs text-gray-500 mt-0.5">Device inventory across workloads</p>
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-5">
            Browse devices in M365, DRMM, and BlackPoint Cyber, or compare coverage across workloads to identify gaps.
          </p>

          <div className="mt-auto flex gap-2">
            <Link
              to="/coverage/devices"
              className="flex-1 flex items-center justify-center gap-2 text-sm font-medium py-2 px-3 rounded-lg bg-purple-600/15 hover:bg-purple-600/25 text-purple-400 border border-purple-500/30 transition-colors"
              title="Device Inventory"
            >
              <ComputerDesktopIcon className="h-4 w-4" />
              Inventory
            </Link>
            <Link
              to="/coverage/comparison"
              className="flex-1 flex items-center justify-center gap-2 text-sm font-medium py-2 px-3 rounded-lg bg-purple-600/15 hover:bg-purple-600/25 text-purple-400 border border-purple-500/30 transition-colors"
              title="Device Inventory Comparison"
            >
              <ArrowsRightLeftIcon className="h-4 w-4" />
              Compare
            </Link>
          </div>
        </div>
      </div>

      {/* Quick actions row */}
      <div className="border-t border-gray-800 pt-5">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Quick Actions</p>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/cases/new"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <PlusCircleIcon className="h-4 w-4" />
            New Investigation
          </Link>
          <Link
            to="/alerts"
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <BellIcon className="h-4 w-4" />
            Review Alerts
          </Link>
        </div>
      </div>
    </div>
  );
}
